import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "@/hooks/use-business";
import { useToast } from "@/hooks/use-toast";
import { Capacitor } from "@capacitor/core";

export type NotificationCounts = Record<string, number>;

// Module-level singletons so state persists across hook re-renders / re-mounts.
let _channelReady = false;
let _lastNativePushAt = 0;

async function sendNativePush(title: string, body?: string | null) {
  if (!Capacitor.isNativePlatform()) return;

  const now = Date.now();
  if (now - _lastNativePushAt < 2000) return; // 2 s throttle for burst events
  _lastNativePushAt = now;

  try {
    const { LocalNotifications } = await import("@capacitor/local-notifications");

    if (!_channelReady) {
      const perms = await LocalNotifications.checkPermissions();
      if (perms.display !== "granted") {
        const req = await LocalNotifications.requestPermissions();
        if (req.display !== "granted") return;
      }

      await LocalNotifications.createChannel({
        id: "buukd-general",
        name: "Buukd Alerts",
        description: "Booking and business alerts",
        importance: 5,
        visibility: 1,
        sound: "default",
        vibration: true,
      });
      _channelReady = true;
    }

    // No `schedule` property → fires immediately; avoids SCHEDULE_EXACT_ALARM requirement on Android 12+
    await LocalNotifications.schedule({
      notifications: [
        {
          id: Math.floor(now % 2_147_483_000),
          title: title || "Buukd",
          body: body || "You have a new update",
          channelId: "buukd-general",
          smallIcon: "ic_stat_icon_config_sample",
          iconColor: "#4f46e5",
        },
      ],
    });
  } catch {
    // Silently ignore; never break the in-app flow
  }
}

export function useNotifications() {
  const { business } = useBusiness();
  const { toast } = useToast();
  const sb = supabase as any;

  const businessId = (business as any)?.id as string | undefined;
  const [counts, setCounts] = useState<NotificationCounts>({});
  const [loading, setLoading] = useState(false);
  const lastToastAtRef = useRef<number>(0);
  const hasHydratedRef = useRef(false);
  const lastTotalRef = useRef(0);

  const refreshCounts = async (silent = false) => {
    if (!businessId) return;
    if (!silent) setLoading(true);
    try {
      const { data, error } = await sb.rpc("get_unread_notification_counts", { _business_id: businessId });
      if (error) throw error;
      const next: NotificationCounts = {};
      (data || []).forEach((r: any) => {
        next[String(r.type)] = Number(r.unread_count || 0);
      });
      setCounts(next);

      const nextTotal = Object.values(next).reduce((sum, v) => sum + Number(v || 0), 0);
      if (hasHydratedRef.current && nextTotal > lastTotalRef.current) {
        const delta = nextTotal - lastTotalRef.current;
        void sendNativePush(
          delta === 1 ? "New activity" : `${delta} new updates`,
          delta === 1 ? "You have a new notification in Buukd." : "You have new notifications in Buukd.",
        );
      }
      hasHydratedRef.current = true;
      lastTotalRef.current = nextTotal;
    } catch {
      // fail silently
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const markTypeRead = async (type: string) => {
    if (!businessId) return;
    try {
      await sb
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("business_id", businessId)
        .eq("type", type)
        .is("read_at", null);
      await refreshCounts();
    } catch {
      // ignore
    }
  };

  const markNotificationRead = async (id: string) => {
    if (!businessId || !id) return;
    try {
      await sb
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("business_id", businessId)
        .eq("id", id)
        .is("read_at", null);
      await refreshCounts();
    } catch {
      // ignore
    }
  };

  const markAllRead = async () => {
    if (!businessId) return;
    try {
      await sb
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("business_id", businessId)
        .is("read_at", null);
      await refreshCounts();
    } catch {
      // ignore
    }
  };

  // Initial fetch
  useEffect(() => {
    refreshCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  // Polling fallback every 25 s to catch missed realtime events
  useEffect(() => {
    if (!businessId) return;
    const timer = window.setInterval(() => void refreshCounts(true), 25_000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  // Supabase realtime subscription
  useEffect(() => {
    if (!businessId) return;

    const channel = sb
      .channel(`notifications:${businessId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications", filter: `business_id=eq.${businessId}` },
        (payload: any) => {
          const n = payload?.new || {};
          const now = Date.now();
          if (now - lastToastAtRef.current > 800) {
            lastToastAtRef.current = now;
            toast({ title: n.title || "New notification", description: n.body || undefined });
          }
          void sendNativePush(n.title || "New notification", n.body || undefined);
          void refreshCounts(true);
        },
      )
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "notifications", filter: `business_id=eq.${businessId}` }, () =>
        void refreshCounts(true),
      )
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "notifications", filter: `business_id=eq.${businessId}` }, () =>
        void refreshCounts(true),
      )
      .subscribe();

    return () => {
      sb.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const totalUnread = useMemo(() => Object.values(counts).reduce((sum, v) => sum + Number(v || 0), 0), [counts]);

  return { counts, totalUnread, loading, refreshCounts, markTypeRead, markNotificationRead, markAllRead };
}
